import { useRef } from "react"
import Flickity from "react-flickity-component"
import { HiArrowUpRight } from "react-icons/hi2"
import { IoChevronBack, IoChevronForward } from "react-icons/io5"

// import "flickity/css/flickity.css"

export interface Announcement {
  id: string | number
  text: string
  href?: string
}

interface AnnouncementBannerProps {
  announcements?: Announcement[]
  autoplayDelay?: number
  className?: string
}

const DEFAULT_ANNOUNCEMENTS: Announcement[] = [
  {
    id: 1,
    text: "Adam Parry of Event Tech Live Joins Premagic as Strategic Advisor",
    href: "https://blog.premagic.com/adam-parry-joins-premagic-as-strategic-advisor/",
  },
  {
    id: 2,
    text: "Premagic shortlisted for Best Use of AI at the Middle East Event Awards 2026",
    href: "https://blog.premagic.com/premagic-shortlisted-middle-east-event-awards-2026/",
  },
  {
    id: 3,
    text: "Premagic has been shortlisted for the Event Technology Awards: AI Edition 2026!",
    href: "https://blog.premagic.com/finalist-premagic-shortlisted-for-the-event-technology-awards-ai-edition-2026/",
  },
]

export default function AnnouncementBanner({
  announcements = DEFAULT_ANNOUNCEMENTS,
  autoplayDelay = 3500,
  className = "",
}: AnnouncementBannerProps) {
  const flickityRef = useRef<any>(null)

  const canLoop = announcements.length > 1

  const flickityOptions = {
    wrapAround: canLoop,
    contain: true,
    cellAlign: "center",
    pageDots: false,
    prevNextButtons: false,
    pauseAutoPlayOnHover: true,
    draggable: canLoop,
    autoPlay: canLoop ? autoplayDelay : false,
    selectedAttraction: 0.025,
    friction: 0.28,
    accessibility: false,
  }

  const handleFlickityRef = (c: any) => {
    flickityRef.current = c
  }

  const handlePrev = () => {
    flickityRef.current?.previous()
  }

  const handleNext = () => {
    flickityRef.current?.next()
  }

  return (
    <div
      className={`news-banner w-full bg-gradient-to-r from-violet-700 via-purple-600 to-violet-500 py-2 px-2 md:px-8 h-[60px] ${className}`}
    >
      <div className="container mx-auto h-full flex items-center">
        {/* Previous Button */}
        <button
          type="button"
          aria-label="Previous announcement"
          onClick={handlePrev}
          className="flex-shrink-0 flex items-center justify-center w-6 h-6 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors mr-2 cursor-pointer z-10"
        >
          <IoChevronBack size={16} />
        </button>

        {/* Slider */}
        <div className="flex-1 min-w-0 overflow-hidden h-full flex items-center">
          <Flickity
            className="w-full h-full"
            elementType="div"
            options={flickityOptions}
            flickityRef={handleFlickityRef}
            disableImagesLoaded
            reloadOnUpdate
          >
            {announcements.map((item) => (
              <div
                key={item.id}
                className="w-full h-[46px] flex items-center justify-center px-2"
              >
                <div className="flex items-center justify-center gap-2 text-center">
                  <span className="flex-shrink-0 flex items-center justify-center w-6 h-6 bg-white/10 rounded-full text-sm select-none">
                    🎉
                  </span>

                  {item.href ? (
                    <a
                      href={item.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 group"
                    >
                      <span className="text-white text-[13.5px] text-left font-medium tracking-wide leading-snug group-hover:underline underline-offset-2 max-sm:text-xs">
                        {item.text}
                      </span>

                      <HiArrowUpRight
                        size={14}
                        className="text-white/80 flex-shrink-0"
                      />
                    </a>
                  ) : (
                    <span className="text-white text-[13.5px] font-medium tracking-wide leading-snug max-sm:text-xs">
                      {item.text}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </Flickity>
        </div>

        {/* Next Button */}
        <button
          type="button"
          aria-label="Next announcement"
          onClick={handleNext}
          className="flex-shrink-0 flex items-center justify-center w-6 h-6 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors ml-2 cursor-pointer z-10"
        >
          <IoChevronForward size={16} />
        </button>
      </div>

      {/* Flickity Fixes */}
      <style>{`
        .flickity-enabled {
          outline: none;
          height: 100%;
        }

        .flickity-viewport {
          overflow: hidden !important;
          height: 100% !important;
        }

        .flickity-slider {
          display: flex;
          align-items: center;
          height: 100%;
        }

        .flickity-slider > div {
          min-height: 46px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
      `}</style>

      {/* Hide on Mobile Menu Open */}
      <style>{`
        .mobile-menu-open .news-banner {
          display: none;
        }
      `}</style>
    </div>
  )
}
